import {
  IClientHasBillingContacts, IClients,
  IGeoCities, IOwnershipGroupContactInfo,
  IOwnershipGroups, IClientsHasUsers,
} from '@llws/lift-entity-interfaces';
import { compactObject, pickNot, mapAsync } from '@jamesgmarks/utilities';
import { Equal, INestedWhereCommand, IQueryCommandOptions, IWhereCommand, convertQueryCommandToQueryString } from '@llws/dynamic-router-utils';
import { adminRequest } from '@/shared/request-configs/admin-request';
import {
  IApiResponse,
  IMetaResponse,
  Nullable,
} from '@/shared';

/**
 * Fetches Client <br>
 * API Endpoint - '/client-api/clients' via {@link adminRequest.get}
 * @param {IQueryCommandOptions} filters - filters query string
 */
export const get = (filters: IQueryCommandOptions) => adminRequest.get('/client-api/clients', convertQueryCommandToQueryString(filters));

/**
 * Fetches Client Cities <br>
 * API Endpoint - `/client-api/clients/${id}/cities?relations=province:p&limit=9999` via {@link adminRequest.get}
 * @param {number} id - id of the client
 */
export const getClientCities = async (id: number): Promise<IApiResponse<IGeoCities[], any>> => (
  adminRequest.get<IGeoCities[]>(
    `/client-api/clients/${id}/cities?relations=province:p&limit=9999&orderBy=cityName~asc`,
  )
);

/**
 * Fetches Client Custom Fields <br>
 * API Endpoint - `/client-api/clients/${id}/custom_fields` via {@link adminRequest.get}
 * @param {number} id - id of the client
 * @param {IQueryCommandOptions} [filters] - optional filters query string
 */
export const getClientCustomFields = (id: number, filters?: IQueryCommandOptions) => adminRequest.get(`/client-api/clients/${id}/custom_fields`, convertQueryCommandToQueryString(filters || {}));

/**
 * Fetches Client Ownership Groups <br>
 * API Endpoint - `/property-api/ownership-groups${q}` via {@link adminRequest.get}
 * @param {number} clientId - id of the client
 * @param {(IWhereCommand | INestedWhereCommand)[]} whereCommands - where filter clause
 */
export const getClientOwnershipGroups = async (
  clientId: number,
  whereCommands: (IWhereCommand | INestedWhereCommand)[] = [],
): Promise<IApiResponse<IOwnershipGroups[], any>> => {
  const q = convertQueryCommandToQueryString({
    relations: [
      {
        path: 'ownershipGroupContactInfos',
        alias: 'contactInfo',
      },
      {
        path: 'contactInfo.freshbooksClient',
        alias: 'client',
      },
    ],
    // example where
    where: [
      Equal('client.id', clientId),
      ...(whereCommands ?? []),
    ],
    orderBy: [
      {
        field: 'name',
        direction: 'asc',
      },
    ],
  });
  return adminRequest.get(`/property-api/ownership-groups${q}`);
};

/**
 * Fetches Client Ownership Group Contacts Infos <br>
 * API Endpoint - `/property-api/ownership-groups${q}` via {@link adminRequest.get}
 * @param {number} id - id of the client
 */
export const getClientOwnershipGroupContactInfo = (id: number) => {
  const q = convertQueryCommandToQueryString({
    relations: [
      {
        path: 'ownershipGroupContactInfos',
        alias: 'contactInfo',
      },
      {
        path: 'contactInfo.freshbooksClient',
        alias: 'client',
      },
    ],
    // example where
    where: [
      Equal('client.id', id),
    ],
  });
  return adminRequest.get(`/property-api/ownership-groups${q}`);
};

/**
 * Fetches Client Has Billing Contacts <br>
 * API Endpoint - `/client-api/client_has_billing_contacts${q}` via {@link adminRequest.get}
 * @param {number} accountingId - freshbooksClientId
 * @param {(IWhereCommand | INestedWhereCommand)[]} whereCriteria - where filter clause
 */
export const getClientHasBillingContacts = async (
  accountingId: number,
  whereCriteria: (IWhereCommand | INestedWhereCommand)[] = [],
): Promise<IClientHasBillingContacts[]> => {
  const q = convertQueryCommandToQueryString({
    relations: [
      {
        path: 'chbc.ownershipGroup',
        alias: 'ownershipGroup',
      },
      {
        path: 'chbc.billingContact',
        alias: 'billingContact',
      },
    ],
    // example where
    alias: 'chbc',
    where: [
      Equal('chbc.freshbooksClientId', accountingId),
      ...whereCriteria,
    ],
  });
  const response = await adminRequest.get(`/client-api/client_has_billing_contacts${q}`) as IApiResponse<IClientHasBillingContacts[], IMetaResponse>;
  return response.data ?? [];
};

/**
 * Fetches Sales Rep of the Client <br>
 * API Endpoint - `/client-api/clients/${id}${q}` via {@link adminRequest.get}
 * @param {number} id - id of the client
 */
export const getClientSalesRep = async (id: number): Promise<IClients> => {
  const q = convertQueryCommandToQueryString({
    relations: [
      {
        path: 'salesRep',
        alias: 'sr',
      },
      {
        path: 'sr.user',
        alias: 'user',
      },
    ],
  });
  const response = await adminRequest.get(`/client-api/clients/${id}${q}`);
  return response.data ?? [];
};

/**
 * Fetches Clients Has Users <br>
 * API Endpoint - `/client-api/clients/${id}/clients_has_users${q}` via {@link adminRequest.get}
 * @param {number} id - id of the client
 */
export const getClientHasUsers = async (id: number): Promise<IClientsHasUsers[]> => {
  const q = convertQueryCommandToQueryString({
    where: [
      Equal('status', 'enabled'),
    ],
  });
  const response = await adminRequest.get(`/client-api/clients/${id}/clients_has_users${q}`);
  return response.data ?? [];
};

/**
 * Fetches Clients Data Sources <br>
 * API Endpoint - `/client-api/clients/${id}/clients_data_sources${q}` via {@link adminRequest.get}
 * @param {number} id - id of the client
 */
export const getClientsDataSources = async (id: number) => {
  const q = convertQueryCommandToQueryString({
    where: [
      Equal('active', '1'),
    ],
  });
  const response = await adminRequest.get(`/client-api/clients/${id}/clients_data_sources${q}`);
  return response.data ?? [];
};

/**
 * Fetches supported languages <br>
 * API Endpoint - `/client-api/languages` via {@link adminRequest.get}
 */
export const getLanguages = async () => {
  const response = await adminRequest.get(`/client-api/languages`);
  return response.data ?? [];
};

/**
 * Saves Client Has Billing Contact <br>
 * API Endpoint - `/client-api/client_has_billing_contacts/${chbc.id}` via {@link adminRequest.put}
 * @param {Partial<IClientHasBillingContacts>} contactData - data to save
 */
export const savePartialClientHasBillingContact = async (contactData: Partial<IClientHasBillingContacts>) => {
  const chbc = compactObject({
    ...pickNot(contactData, ['billingContact', 'created', 'ownershipGroup']),
  }, true) as IClientHasBillingContacts;

  if (!chbc.id) { throw new Error('Partial save must have id.'); }

  return adminRequest.put(`/client-api/client_has_billing_contacts/${chbc.id}`, chbc);
};

/**
 * Saves Billing Contact <br>
 * API Endpoints:
 * <li>`/client-api/billing_contacts/${billingContact.id}` via {@link adminRequest.put}</li>
 * <li>`/client-api/billing_contacts/` via {@link adminRequest.post}</li>
 * <li>`/client-api/client_has_billing_contacts/${chbcPart.id}` via {@link adminRequest.put}</li>
 * <li>`/client-api/client_has_billing_contacts/` via {@link adminRequest.post}</li>
 * @param {IClientHasBillingContacts} contactData - contact to save
 */
export const saveBillingContact = async (contactData: IClientHasBillingContacts) => {
  const billingContact = {
    ...pickNot(contactData.billingContact!, ['created']),
  };

  let billingContactSaveResult: Nullable<IApiResponse<any, any>> = null;
  try {
    billingContactSaveResult = (
      billingContact // eslint-disable-line no-nested-ternary
        ? (
          billingContact?.id
            ? await adminRequest.put(`/client-api/billing_contacts/${billingContact.id}`, billingContact)
            : await adminRequest.post('/client-api/billing_contacts/', billingContact)
        )
        : null
    );
  } catch (error) {
    throw new Error(`Billing contact did not successfully save. Error: ${JSON.stringify(error)}`);
  }

  const billingContactSaveData = (
    billingContactSaveResult && Array.isArray(billingContactSaveResult?.data)
      ? billingContactSaveResult.data[0]
      : billingContactSaveResult?.data
  );

  const { ownershipGroup, ownershipGroupId } = contactData;

  let chbcPart: IClientHasBillingContacts;
  let chbcResult: Nullable<IApiResponse<any, any>> = null;
  if (billingContactSaveData?.id) {
    chbcPart = compactObject({
      ...pickNot(contactData, ['billingContact', 'created', 'ownershipGroup']),
      billingContactId: billingContactSaveData.id,
      ownershipGroupId: ownershipGroupId ?? ownershipGroup?.id ?? null,
    }, true) as IClientHasBillingContacts;

    chbcResult = (
      chbcPart.id
        ? await adminRequest.put(`/client-api/client_has_billing_contacts/${chbcPart.id}`, chbcPart)
        : await adminRequest.post('/client-api/client_has_billing_contacts', chbcPart)
    );
  }

  return ({
    results: {
      clientHasBillingContacts: (
        chbcResult?.data
          ? chbcResult.data as IClientHasBillingContacts
          : chbcResult as IApiResponse<IClientHasBillingContacts, IMetaResponse> | null
      ),
      billingContacts: (
        billingContactSaveResult?.data
          ? billingContactSaveResult.data as IClientHasBillingContacts
          : billingContactSaveResult as IApiResponse<IClientHasBillingContacts, IMetaResponse> | null
      ),
    },
  });
};

/**
 * Saves Ownership Group & Saves Contact Results Afterwards <br>
 * API Endpoints:
 * <li>`/client-api/ownership_groups/` via {@link adminRequest.post}</li>
 * <li>`/client-api/ownership_groups/${ownershipGroup.id}` via {@link adminRequest.put}</li>
 * <li>`/client-api/ownership_groups/${ownershipGroupId}/ownership_group_contact_info/${contactWithClientId.id}` via {@link adminRequest.put}</li>
 * <li>`/client-api/ownership_groups/${ownershipGroupId}/ownership_group_contact_info` via {@link adminRequest.post}</li>
 * @param {IOwnershipGroups} ownershipGroup - ownership group to save
 * @param {IClients} currentClient - current client
 */
export const saveOwnershipGroup = async (ownershipGroup: IOwnershipGroups, currentClient: IClients) => {
  const ogSavePayload = { ...ownershipGroup, freshbooksClientId: currentClient.freshbooksClientId };

  const saveResult = (
    ownershipGroup.id
      ? await adminRequest.put(`/client-api/ownership_groups/${ownershipGroup.id}`, ogSavePayload)
      : await adminRequest.post('/client-api/ownership_groups', ogSavePayload)
  );

  const ownershipGroupId = Array.isArray(saveResult.data) ? saveResult.data[0].id : saveResult.data.id;
  const contactInfos = ownershipGroup.ownershipGroupContactInfos ?? [];

  const saveContactResults = (
    ownershipGroupId
      ? await mapAsync(contactInfos, (contact) => {
        const contactWithClientId = {
          ...contact,
          freshbooksClientId: currentClient.freshbooksClientId,
        } as IOwnershipGroupContactInfo;
        return (
          contactWithClientId.id
            ? adminRequest.put(`/client-api/ownership_groups/${ownershipGroupId}/ownership_group_contact_info/${contactWithClientId.id}`, contactWithClientId)
            : adminRequest.post(`/client-api/ownership_groups/${ownershipGroupId}/ownership_group_contact_info`, contactWithClientId)
        );
      })
      : null
  );

  return {
    ownershipGroup: saveResult,
    contacts: saveContactResults,
  };
};

export default {
  get,
  getClientCities,
  getClientCustomFields,
  getClientOwnershipGroups,
  getClientHasBillingContacts,
  saveBillingContact,
  saveOwnershipGroup,
  savePartialClientHasBillingContact,
};
